// Il disegno di PopTown su canvas (artboard 56). Un pop è la sua immagine tagliata in tre: il
// corpo e i due piedi. Camminando i piedi si alzano e avanzano a turno e il corpo ondeggia appena,
// senza saltelli; da fermo i tre pezzi combaciano. Chi è più in basso è più vicino: più grande e
// disegnato dopo, cioè davanti.
import { depth, type Pop, type Town } from './sim';
import { rigFor, type Sprite } from './sprite';
import { WORLD } from './walk';

export { loadSprite, rigFor, type Sprite } from './sprite';

export interface View {
  scale: number;
  ox: number;
  oy: number;
  /** pixel del canvas per pixel dello schermo: testi e linee si misurano in pixel veri */
  dpr: number;
}

/** quanto è alto un pop, in pixel della città, a metà profondità */
const HEIGHT = 90;

export const LAMPS: [number, number][] = [
  [355, 265],
  [463, 150],
  [545, 188],
  [878, 140],
  [1190, 183],
  [1435, 333],
  [1520, 457],
  [1432, 648],
  [1628, 708],
  [1290, 775],
  [690, 812],
  [106, 664],
  [117, 494],
];

export function spriteFor(sprites: Map<string, Sprite>, p: Pop): Sprite | undefined {
  return sprites.get(rigFor(p.role, p.variant).id);
}

function drawPop(ctx: CanvasRenderingContext2D, s: Sprite, p: Pop, v: View, time: number) {
  const r = s.rig;
  const k = (HEIGHT * depth(p.y) * v.scale) / r.h;
  const fx = p.x * v.scale + v.ox;
  const fy = p.y * v.scale + v.oy;
  // ombra a terra
  ctx.fillStyle = 'rgba(30, 16, 60, 0.32)';
  ctx.beginPath();
  ctx.ellipse(fx, fy, r.w * 0.32 * k, r.h * 0.035 * k, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.save();
  ctx.translate(fx, fy);
  ctx.scale(p.facing * k, k);
  ctx.translate(-r.w / 2, -r.h);
  // le gambe, dietro: si alzano a turno, viste di fronte; il corpo copre la parte che sale. Un
  // poco di variazione morbida, diversa per pop e per passo (inclinazione di pochi gradi, scarto
  // di lato, altezza del passo), così il passo non è rigido
  for (const [img, [x, y, w, h], shift, seed] of [
    [s.legL, r.legL, 0, p.id * 1.7],
    [s.legR, r.legR, Math.PI, p.id * 2.3 + 1],
  ] as const) {
    const t = p.stride + shift;
    const wobble = p.moving ? 1 : 0;
    const lift = wobble * Math.max(0, Math.sin(t)) * h * (0.19 + 0.04 * Math.sin(t * 0.37 + seed));
    const tilt =
      wobble * (Math.sin(t * 0.53 + seed) * 0.6 + Math.sin(t * 1.31 + seed * 2) * 0.4) * 0.05;
    const drift = wobble * Math.sin(t * 0.71 + seed * 3) * w * 0.025;
    ctx.save();
    ctx.translate(x + w / 2 + drift, y + h * 0.2 - lift);
    ctx.rotate(tilt);
    ctx.drawImage(img, -w / 2, -h * 0.2, w, h);
    ctx.restore();
  }
  // il corpo, sopra: fermo mentre cammina, da fermo respira appena
  const [bx, by, bw, bh] = r.body;
  ctx.save();
  if (!p.moving) {
    const breathe = Math.sin(time * 2 + p.id) * 0.008;
    ctx.translate(bx + bw / 2, by + bh);
    ctx.scale(1, 1 + breathe);
    ctx.translate(-(bx + bw / 2), -(by + bh));
  }
  ctx.drawImage(s.body, bx, by, bw, bh);
  ctx.restore();
  ctx.restore();
}

/** La vignetta a nuvola: sbuffi tondi attorno al testo e, al posto della punta, due palline sempre
 * più piccole che scendono verso chi parla. */
function bubble(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size: number) {
  ctx.font = `700 ${size}px -apple-system, "Segoe UI", Inter, Roboto, Arial, sans-serif`;
  const tw = ctx.measureText(text).width;
  const w = tw + size * 1.6;
  const h = size * 2;
  const cx = Math.round(x);
  const cy = Math.round(y - h / 2 - size * 1.5);
  const puff = h * 0.42;
  const circles: [number, number, number][] = [];
  // sbuffi in alto e in basso, lungo tutta la larghezza
  const n = Math.max(2, Math.round(w / (puff * 1.25)));
  for (let i = 0; i <= n; i++) {
    const px = cx - w / 2 + (w * i) / n;
    const wobble = 1 + 0.12 * Math.sin(i * 2.3);
    circles.push([px, cy - h * 0.32, puff * wobble]);
    circles.push([px, cy + h * 0.32, puff * (2 - wobble) * 0.92]);
  }
  // i due fianchi tondi
  circles.push([cx - w / 2 - puff * 0.25, cy, puff * 1.05]);
  circles.push([cx + w / 2 + puff * 0.25, cy, puff * 1.05]);
  // la coda: due palline verso la testa
  const tail: [number, number, number][] = [
    [cx - size * 0.2, cy + h / 2 + size * 0.75, size * 0.36],
    [cx - size * 0.45, cy + h / 2 + size * 1.3, size * 0.22],
  ];
  const paint = (dy: number, color: string) => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.roundRect(cx - w / 2, cy - h / 2 + dy, w, h, h / 2);
    ctx.fill();
    for (const [px, py, r] of [...circles, ...tail]) {
      ctx.beginPath();
      ctx.arc(px, py + dy, r, 0, Math.PI * 2);
      ctx.fill();
    }
  };
  paint(size * 0.22, 'rgba(20, 10, 40, 0.25)');
  paint(0, '#ffffff');
  ctx.fillStyle = '#1d1640';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, cx, cy + 1);
}

/** Un fotogramma: città, luci, pop dal più lontano al più vicino, palle, nuvolette. */
export function drawFrame(
  ctx: CanvasRenderingContext2D,
  ground: HTMLImageElement,
  sprites: Map<string, Sprite>,
  town: Town,
  v: View,
  time: number,
) {
  ctx.drawImage(ground, v.ox, v.oy, WORLD.w * v.scale, WORLD.h * v.scale);
  ctx.globalCompositeOperation = 'screen';
  LAMPS.forEach(([x, y], i) => {
    const r = 26 * v.scale * (0.85 + 0.15 * Math.sin(time * 1.7 + i * 1.3));
    const g = ctx.createRadialGradient(
      x * v.scale + v.ox,
      y * v.scale + v.oy,
      0,
      x * v.scale + v.ox,
      y * v.scale + v.oy,
      r,
    );
    g.addColorStop(0, 'rgba(255, 214, 140, 0.55)');
    g.addColorStop(1, 'rgba(255, 190, 90, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(x * v.scale + v.ox - r, y * v.scale + v.oy - r, r * 2, r * 2);
  });
  for (let i = 0; i < 14; i++) {
    const t = (time / (8 + (i % 5)) + i * 0.137) % 1;
    const x = (((i * 619) % WORLD.w) + Math.sin(time * 0.6 + i) * 18) * v.scale + v.ox;
    const y = (380 + ((i * 271) % 520) - t * 120) * v.scale + v.oy;
    ctx.fillStyle = `rgba(255, 240, 180, ${Math.sin(t * Math.PI) * 0.85})`;
    ctx.beginPath();
    ctx.arc(x, y, 2.2 * v.scale + 0.6, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalCompositeOperation = 'source-over';
  const order = [...town.pops].sort((a, b) => a.y - b.y);
  for (const p of order) {
    const s = spriteFor(sprites, p);
    if (s) drawPop(ctx, s, p, v, time);
  }
  for (const ball of town.balls) {
    const x = ball.from.x + (ball.to.x - ball.from.x) * ball.t;
    const base = ball.from.y + (ball.to.y - ball.from.y) * ball.t - 46;
    const y = base - Math.sin(Math.PI * ball.t) * 70;
    const r = 7 * depth(base) * v.scale;
    const g = ctx.createRadialGradient(
      x * v.scale + v.ox - r / 3,
      y * v.scale + v.oy - r / 3,
      1,
      x * v.scale + v.ox,
      y * v.scale + v.oy,
      r,
    );
    g.addColorStop(0, '#ffe48a');
    g.addColorStop(1, '#e08a10');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x * v.scale + v.ox, y * v.scale + v.oy, r, 0, Math.PI * 2);
    ctx.fill();
  }
  const size = 19 * v.dpr;
  for (const p of order) {
    if (!p.bubble) continue;
    const top = (p.y - HEIGHT * depth(p.y)) * v.scale + v.oy;
    bubble(ctx, p.bubble.text, p.x * v.scale + v.ox, top, size);
  }
}
